/**
 * Centralised date / time formatting helpers.
 *
 * Pure functions (for non-React code) and a React hook that returns
 * locale-bound formatters so callers never need to pass the locale manually.
 *
 * @example
 * ```tsx
 * import { useDateFormat } from '../utils/dateFormat';
 *
 * const { formatDateTime, formatDate, formatTime } = useDateFormat();
 * formatDateTime(someDate); // "01/02/2026 14:30:06"
 * ```
 */

import { useAppLocale } from '../hooks/useAppLocale';

type DateInput = string | number | Date;

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

/** Full date + time  – e.g. "01/02/2026 14:30:06" (pt-BR) */
export function formatDateTime(value: DateInput, locale: string): string {
  return toDate(value).toLocaleString(locale);
}

/** Date only – e.g. "01/02/2026" (pt-BR) */
export function formatDate(value: DateInput, locale: string): string {
  return toDate(value).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Time only – e.g. "14:30" */
export function formatTime(value: DateInput, locale: string): string {
  return toDate(value).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get a human-readable relative time string from a date.
 * Converts timestamps to short labels: "Just now", "5m ago", "3h ago", "Yesterday", "3d ago", etc.
 *
 * @param dateString - ISO date string (or null/undefined)
 * @param translate  - react-admin translate function for i18n
 * @param locale     - locale string for date formatting fallback (e.g. 'pt-BR')
 */
export function getRelativeTime(
  dateString: string | null | undefined,
  translate: (key: string, options?: Record<string, unknown>) => string,
  locale?: string,
): string {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return translate('resources.chat.time.justNow');
  }
  if (diffMins < 60) {
    return translate('resources.chat.time.minutesAgo', { count: diffMins });
  }
  if (diffHours < 24) {
    return translate('resources.chat.time.hoursAgo', { count: diffHours });
  }
  if (diffDays === 1) {
    return translate('resources.chat.time.yesterday');
  }
  if (diffDays < 7) {
    return translate('resources.chat.time.daysAgo', { count: diffDays });
  }

  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Hook that returns date formatting functions already bound to the
 * current app locale. No need to pass locale on every call.
 */
export function useDateFormat() {
  const locale = useAppLocale();

  return {
    formatDateTime: (value: DateInput) => formatDateTime(value, locale),
    formatDate: (value: DateInput) => formatDate(value, locale),
    formatTime: (value: DateInput) => formatTime(value, locale),
  };
}
